// Pure logic for the connection config view, extracted so it can be unit-tested
// without rendering the component.

import { type IntegrationKind } from "./integrations-data";

// Connection detail tabs. Every connection gets Automations and Settings; the
// inbound-capable kinds add a Webhook tab (the inbound URL), and a provider that
// declares Setup Wizard fields adds Setup.
export type DetailTab = "setup" | "automations" | "features" | "webhook" | "settings";

// Kinds whose connections receive inbound webhooks. Every provider — CRM and
// ingestion alike — now mints a per-connection routing token and resolves inbound
// requests by it, so both get the tab. Typed against the schema enum, so a typo
// or a renamed kind is a compile error rather than a tab that silently vanishes.
const INBOUND_KINDS: IntegrationKind[] = ["crm", "ingestion"];

/**
 * The Webhook tab is deliberately NOT gated on the connection already having a
 * URL: the tab is where you go to get one. A connection created before token
 * minting existed has no `webhookUrl`, and the panel offers to generate it.
 *
 * The Setup (Configuration Wizard) tab IS gated, on the provider declaring setup
 * fields. Gate on the DATA rather than on a list of provider slugs: a provider
 * that maps nothing must
 * not show a tab that can only ever say "Coming soon", and the next such
 * provider needs no edit here. No provider declares setup fields today, so the
 * tab is currently hidden everywhere; it reappears by itself when one does,
 * which is also when its panel has to stop being a placeholder.
 */
export function tabsForProvider(
  provider: {
    kind: IntegrationKind;
    setup: readonly unknown[];
    remoteCollections: readonly unknown[];
  },
  viewer: { canReadRemoteOptions: boolean },
): DetailTab[] {
  const setup: DetailTab[] = provider.setup.length > 0 ? ["setup"] : [];
  // Straight after Automations, gated on the provider declaring something to
  // browse — same rule as Setup — AND on the viewer being able to read it.
  //
  // The permission half is not belt-and-braces. This tab's only query,
  // `integrationRemoteOptions`, is gated on `automations.manage`, deliberately:
  // it also feeds the automation field picker, and the author who needs that
  // picker frequently lacks `workspace.integrations`. But reaching THIS tab
  // needs `workspace.integrations`, and the two keys are independently
  // grantable. Without this check a role holding one and not the other is
  // offered a tab that is visible, clickable, and can only ever say "Could not
  // load this list."
  //
  // Hiding is the conservative resolution, and it makes the UI honest. If a
  // connection manager SHOULD be able to browse without authoring automations,
  // the fix belongs on the server — letting that query accept either key — not
  // here.
  const features: DetailTab[] =
    provider.remoteCollections.length > 0 && viewer.canReadRemoteOptions ? ["features"] : [];
  const webhook: DetailTab[] = INBOUND_KINDS.includes(provider.kind) ? ["webhook"] : [];
  return [...setup, "automations", ...features, ...webhook, "settings"];
}

// Split a comma/newline-joined value into trimmed, non-empty entries.
export function splitEntries(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Canonical form stored on the server / sent to the API: comma-separated,
// blanks dropped. Used at the test/save boundary only.
export function joinEntries(rows: string[]): string {
  return rows
    .map((r) => r.trim())
    .filter((r) => r.length > 0)
    .join(", ");
}

// The in-editor buffer for a "list" field preserves row structure — including a
// pending blank row — so adding/removing rows registers as an edit (dirty), and
// Undo collapses it. Newline-joined; the ListEditor round-trips it verbatim.
export function listBuffer(stored: string): string {
  return splitEntries(stored).join("\n");
}

// Seed the form's value map from loaded credential fields, normalizing "list"
// fields into their newline editing buffer so dirty-checking compares like with
// like. Shared by the initial seed and Undo (handleReset).
export function seedValuesFromFields(
  fields: ReadonlyArray<{ key: string; value: string; control?: string | null | undefined }>,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const f of fields) {
    next[f.key] = f.control === "list" ? listBuffer(f.value) : f.value;
  }
  return next;
}

// Canonicalize a single field's edit buffer for the API (list → comma form with
// blanks dropped; everything else passes through unchanged).
export function canonicalFieldValue(
  field: { control?: string | null | undefined },
  value: string,
): string {
  return field.control === "list" ? joinEntries(splitEntries(value)) : value;
}

// Unsaved-changes check against the on-load baseline. List fields compare their
// newline buffers (so a pending blank row counts as dirty); other fields compare
// the raw string. Name change also marks dirty.
export function settingsDirty(
  fields: ReadonlyArray<{ key: string; value: string; control?: string | null | undefined }>,
  values: Record<string, string>,
  name: string,
  initialName: string,
): boolean {
  if (name !== initialName) return true;
  return fields.some((f) => {
    const current = values[f.key] ?? "";
    const baseline = f.control === "list" ? listBuffer(f.value) : f.value;
    return current !== baseline;
  });
}

// Provider-agnostic co-requirement: fields sharing a `group` are all-or-nothing.
// If some are filled and others blank, return a message; null when satisfied.
// The grouping is declared in the provider's field metadata (`AuthField.group`).
export function fieldGroupError(
  fields: ReadonlyArray<{ key: string; label: string; group?: string | null | undefined }>,
  values: Record<string, string>,
): string | null {
  const groups = new Map<string, { key: string; label: string }[]>();
  for (const f of fields) {
    if (!f.group) continue;
    const list = groups.get(f.group) ?? [];
    list.push({ key: f.key, label: f.label });
    groups.set(f.group, list);
  }
  for (const groupFields of groups.values()) {
    if (groupFields.length < 2) continue;
    const filled = groupFields.filter((f) => (values[f.key] ?? "").trim().length > 0).length;
    if (filled > 0 && filled < groupFields.length) {
      const labels = groupFields.map((f) => f.label).join(" and ");
      return `Set ${labels} together, or leave them blank.`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Connection Setup footer tooltips
// ---------------------------------------------------------------------------

/** Everything the Connection Setup footer's two buttons derive their copy from —
 *  the same flags that drive their `disabled` props, so a tooltip can't claim a
 *  reason the button doesn't actually have. */
export type SettingsActionState = {
  /** A test request is in flight. */
  testing: boolean;
  /** A save request is in flight. */
  saving: boolean;
  /** The current values passed a test and no edit has invalidated it. */
  tested: boolean;
  /** The form differs from what it loaded with. */
  isDirty: boolean;
  /** Connection name is blank (blocks Test). */
  nameEmpty: boolean;
  /** Cross-field validation message, or null (blocks Test). */
  validationError: string | null;
};

/** Tooltip for Test Connection. In-flight and blocking states first, so the
 *  message names the reason the button is actually disabled; the validation
 *  message is echoed verbatim because it's the specific fix needed. */
export function testButtonTooltip(state: SettingsActionState): string {
  if (state.testing) return "Checking these settings against the provider…";
  if (state.saving) return "Waiting for the save to finish.";
  if (state.nameEmpty) return "Give the connection a name first.";
  if (state.validationError) return state.validationError;
  if (state.isDirty) return "Check your unsaved changes against the provider.";
  if (state.tested) return "Settings passed. Check them again if you like.";
  return "Check the saved settings against the provider.";
}

/** Tooltip for Save Changes. The `!tested` branches carry the weight: Save is
 *  gated on a passing test, and the button alone can't explain that. */
export function saveButtonTooltip(state: SettingsActionState): string {
  if (state.saving) return "Saving your changes…";
  if (state.testing) return "Waiting for the test to finish.";
  if (!state.tested) {
    return state.isDirty
      ? "Test your changes first — Save unlocks once they pass."
      : "Run Test Connection to enable saving.";
  }
  return state.isDirty
    ? "Save your changes."
    : "Re-save these settings — nothing has changed.";
}

/** The always-visible hint beside the Connection Setup footer buttons: what to
 *  do next, in order of what's blocking. Distinct from the tooltips above — a
 *  tooltip explains one button on hover, this states the next step outright. */
export function settingsNextStep(state: SettingsActionState): string {
  if (state.saving) return "Saving your changes…";
  if (state.testing) return "Testing the connection…";
  if (state.nameEmpty) return "Give the connection a name to continue.";
  if (state.validationError) return "Resolve the field error above, then test the connection.";
  if (state.tested) {
    return state.isDirty
      ? "Test passed — save your changes."
      : "Tested. There are no changes to save.";
  }
  return state.isDirty
    ? "Test your changes, then save them."
    : "No changes yet. Edit a field, or test the settings as saved.";
}

// ---------------------------------------------------------------------------
// New connection footer
// ---------------------------------------------------------------------------

/** The new-connection form's equivalent of `SettingsActionState`. There's no
 *  dirty/clean axis here (everything is new) and no saved name to blank out —
 *  `allFilled` covers the name plus every required auth field. */
export type NewConnectionActionState = {
  testing: boolean;
  saving: boolean;
  tested: boolean;
  /** Name and every required auth field have a value. */
  allFilled: boolean;
  /** Cross-field validation message, or null. */
  validationError: string | null;
};

/** Tooltip for the new-connection Test Connection button. */
export function newTestButtonTooltip(state: NewConnectionActionState): string {
  if (state.testing) return "Checking these credentials against the provider…";
  if (state.saving) return "Waiting for the connection to be created.";
  if (!state.allFilled) return "Fill in the name and every required field first.";
  if (state.validationError) return state.validationError;
  return "Check these credentials against the provider.";
}

/** Tooltip for the new-connection Save Connection button. It only renders once
 *  the test passes (the form swaps Test out for it), so there's no "test first"
 *  branch to write — the note beside it carries that instruction instead. */
export function newSaveButtonTooltip(state: NewConnectionActionState): string {
  if (state.saving) return "Creating the connection…";
  return "Create the connection with these settings.";
}

/** The hint beside the new-connection footer buttons: fill in → test → save. */
export function newConnectionNextStep(state: NewConnectionActionState): string {
  if (state.saving) return "Creating the connection…";
  if (state.testing) return "Testing the connection…";
  if (state.tested) return "Test passed — save to create the connection.";
  if (!state.allFilled) return "Enter the connection details, then test the connection.";
  if (state.validationError) return "Resolve the field error above, then test the connection.";
  return "Test the connection to continue.";
}

// Key-order-insensitive JSON serialization, for identity checks on a payload
// whose key order isn't meaningful — `useConnectionSettings` keys its re-seed
// guard on it, so a background refetch returning the same credential preview
// doesn't discard in-progress edits.
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeysDeep(value));
}
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      out[k] = sortKeysDeep((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}
